/**
 * Structured activity codes: {YEAR}-{TYPE}-{SEQ}
 * Example: 2026-GAME-001, 2026-HLTH-014, 2026-EDU-003
 */
import { ACTIVITY_TYPE_OPTIONS } from './activity-type-domain.map';

export const ACTIVITY_TYPE_CODE_MAP: Record<string, string> = {
  Game: 'GAME',
  'Health & Wellness': 'HLTH',
  Educational: 'EDU',
};

const CODE_PATTERN = /^(\d{4})-([A-Z]{2,8})-(\d{3,})$/i;

export function activityTypeCodeAbbreviation(type: string | null | undefined): string | null {
  const key = String(type ?? '').trim();
  if (!key) {
    return null;
  }
  const exact = ACTIVITY_TYPE_CODE_MAP[key];
  if (exact) {
    return exact;
  }
  const matched = ACTIVITY_TYPE_OPTIONS.find((option) => option.toLowerCase() === key.toLowerCase());
  return matched ? ACTIVITY_TYPE_CODE_MAP[matched] ?? null : null;
}

export function buildActivityCodePrefix(
  type: string | null | undefined,
  year: number = new Date().getFullYear(),
): string | null {
  const abbr = activityTypeCodeAbbreviation(type);
  if (!abbr || !Number.isFinite(year)) {
    return null;
  }
  return `${Math.trunc(year)}-${abbr}`;
}

export function formatActivityCode(prefix: string, sequence: number): string {
  const seq = Math.max(1, Math.trunc(sequence));
  return `${prefix}-${String(seq).padStart(3, '0')}`;
}

/**
 * Next sequence for a Year-Type prefix from existing activity codes.
 * Ignores codes that do not match the structured nomenclature.
 */
export function nextActivityCodeSequence(
  existingCodes: Array<string | null | undefined>,
  prefix: string,
): number {
  const normalizedPrefix = prefix.trim().toUpperCase();
  let max = 0;
  for (const raw of existingCodes) {
    const code = String(raw ?? '').trim().toUpperCase();
    if (!code.startsWith(`${normalizedPrefix}-`)) {
      continue;
    }
    const match = CODE_PATTERN.exec(code);
    if (!match) {
      continue;
    }
    const [, year, type, seqText] = match;
    if (`${year}-${type}` !== normalizedPrefix) {
      continue;
    }
    const seq = Number(seqText);
    if (Number.isFinite(seq) && seq > max) {
      max = seq;
    }
  }
  return max + 1;
}

export function generateNextActivityCode(
  type: string | null | undefined,
  existingCodes: Array<string | null | undefined>,
  year: number = new Date().getFullYear(),
): string | null {
  const prefix = buildActivityCodePrefix(type, year);
  if (!prefix) {
    return null;
  }
  return formatActivityCode(prefix, nextActivityCodeSequence(existingCodes, prefix));
}

export function isStructuredActivityCode(value: string | null | undefined): boolean {
  return CODE_PATTERN.test(String(value ?? '').trim());
}
